import { memo, useCallback, useMemo } from "react"
import { Security } from '../../proto/generated/security'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useStyles } from "./styles"
import { getSecurityKey } from "../../util/security"
import { toLocaleStringIn } from "../../util/general"
import { useDispatch } from "react-redux"
import { setQuickOrderData } from "../../reducer/orders.reducer"
import { Broker_OrderType, OrderType, Product, TransactionType } from "../../proto/generated/order"
import { isTradeableSecurity } from "../../util/cashInstrument"
import {
    useHasOrderBook,
    useOrderBookQuoteCount,
    useOrderBookQuoteField,
    useOrderBookSideMaxQuantity,
    useOrderBookSideTotal,
} from "../../hooks/useSecurityMarketTicks"
import store from "../../store"
import { primary, red } from "../../assets/colors"

export type MarketDepthProps = {
    security: Security,
    bidAskCentered?: boolean,
    tradeable?: boolean
}

const EMPTY_DEPTH_ROWS = 5

type DepthSide = 'bid' | 'ask'

const BID_GRADIENT = 'rgba(65, 132, 243, 0.1)'
const ASK_GRADIENT = 'rgba(255, 87, 34, 0.1)'

const formatBuySellPct = (value: number) =>
    `${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`

const BuySellOrdersBar = memo(function BuySellOrdersBar({ securityKey }: { securityKey: string }) {
    const buyQty = useOrderBookSideTotal(securityKey, 'bid')
    const sellQty = useOrderBookSideTotal(securityKey, 'ask')
    const total = buyQty + sellQty
    const buyPct = total <= 0 ? 50 : (buyQty / total) * 100
    const sellPct = 100 - buyPct

    return (
        <Box width="100%" px={0.5} pt={0.5} pb={1}>
            <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography
                    variant="caption"
                    className="font-feature-num"
                    sx={{ color: 'text.primary', fontSize: 12, lineHeight: 1.2 }}
                >
                    {formatBuySellPct(buyPct)}
                </Typography>
                <Typography
                    variant="caption"
                    className="font-feature-num"
                    sx={{ color: 'text.primary', fontSize: 12, lineHeight: 1.2 }}
                >
                    {formatBuySellPct(sellPct)}
                </Typography>
            </Box>
            <Box
                display="flex"
                width="100%"
                height={4}
                borderRadius={1}
                overflow="hidden"
                sx={{ bgcolor: (theme) => theme.palette.action.hover }}
            >
                <Box sx={{ width: `${buyPct}%`, bgcolor: primary }} />
                <Box sx={{ width: `${sellPct}%`, bgcolor: red }} />
            </Box>
        </Box>
    )
})

type LiveFieldProps = {
    securityKey: string
    side: DepthSide
    index: number
}

const DepthPriceValue = memo(function DepthPriceValue({
    securityKey,
    side,
    index,
    priceMultiplier,
}: LiveFieldProps & { priceMultiplier: number }) {
    const price = useOrderBookQuoteField(securityKey, side, index, 'price')
    return (
        <span className="font-feature-num">
            {price != null ? toLocaleStringIn(price / priceMultiplier) : '0.00'}
        </span>
    )
})

const DepthOrdersValue = memo(function DepthOrdersValue({ securityKey, side, index }: LiveFieldProps) {
    const numberOfOrders = useOrderBookQuoteField(securityKey, side, index, 'numberOfOrders')
    return <span className="font-feature-num">{numberOfOrders ?? 0}</span>
})

const DepthQuantityValue = memo(function DepthQuantityValue({
    securityKey,
    side,
    index,
}: LiveFieldProps) {
    const quantity = useOrderBookQuoteField(securityKey, side, index, 'quantity')
    const maxQuantity = useOrderBookSideMaxQuantity(securityKey, side)
    const shadowWidth = maxQuantity > 0 ? ((quantity ?? 0) / maxQuantity) * 100 : 0
    const gradientColor = side === 'bid' ? BID_GRADIENT : ASK_GRADIENT

    return (
        <div style={{ paddingLeft: 8, height: '100%' }}>
            <div
                style={{
                    height: '100%',
                    background: `linear-gradient(to left, ${gradientColor} ${shadowWidth}%, transparent ${shadowWidth}%)`,
                }}
            >
                <span className="font-feature-num">{quantity ?? 0}</span>
            </div>
        </div>
    )
})

const DepthTotalValue = memo(function DepthTotalValue({
    securityKey,
    side,
}: {
    securityKey: string
    side: DepthSide
}) {
    const total = useOrderBookSideTotal(securityKey, side)
    return <span className="font-feature-num">{total}</span>
})

type MarketDepthRowShellProps = {
    securityKey: string
    side: DepthSide
    index: number
    priceMultiplier: number
    bidAskCentered: boolean
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}

const MarketDepthRowShell = memo(function MarketDepthRowShell({
    securityKey,
    side,
    index,
    priceMultiplier,
    bidAskCentered,
    tradeable,
    onRowClick,
}: MarketDepthRowShellProps) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell

    const handleClick = useCallback(() => {
        if (!tradeable) return
        const book = store.getState().securityStore.orderBooks[securityKey]
        const quotes = side === 'bid' ? book?.bids : book?.asks
        onRowClick(side, quotes?.[index]?.price ?? 0)
    }, [securityKey, side, index, tradeable, onRowClick])

    const priceCell = (
        <TableCell key="price" className={cx(classes.rowCell, sideClass)}>
            <DepthPriceValue
                securityKey={securityKey}
                side={side}
                index={index}
                priceMultiplier={priceMultiplier}
            />
        </TableCell>
    )
    const ordersCell = (
        <TableCell key="orders" className={cx(classes.rowCell, sideClass)}>
            <DepthOrdersValue securityKey={securityKey} side={side} index={index} />
        </TableCell>
    )
    const qtyCell = (
        <TableCell key="qty" className={cx(classes.rowCell, sideClass)}>
            <DepthQuantityValue securityKey={securityKey} side={side} index={index} />
        </TableCell>
    )

    const cells = bidAskCentered ? [qtyCell, ordersCell, priceCell] : [priceCell, ordersCell, qtyCell]

    return (
        <TableRow
            className={cx(classes.tableRow, { [classes.tradeable]: tradeable })}
            onClick={tradeable ? handleClick : undefined}
        >
            {cells}
        </TableRow>
    )
})

type MarketDepthEmptyRowProps = {
    side: DepthSide
    bidAskCentered: boolean
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}

const MarketDepthEmptyRow = memo(function MarketDepthEmptyRow({
    side,
    bidAskCentered,
    tradeable,
    onRowClick,
}: MarketDepthEmptyRowProps) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell

    const priceCell = (
        <TableCell key="price" className={cx(classes.rowCell, sideClass)}>
            <span className="font-feature-num">0.00</span>
        </TableCell>
    )
    const ordersCell = (
        <TableCell key="orders" className={cx(classes.rowCell, sideClass)}>
            <span className="font-feature-num">0</span>
        </TableCell>
    )
    const qtyCell = (
        <TableCell key="qty" className={cx(classes.rowCell, sideClass)}>
            <span className="font-feature-num">0</span>
        </TableCell>
    )

    const cells = bidAskCentered ? [qtyCell, ordersCell, priceCell] : [priceCell, ordersCell, qtyCell]

    return (
        <TableRow
            className={cx(classes.tableRow, { [classes.tradeable]: tradeable })}
            onClick={tradeable ? () => onRowClick(side, 0) : undefined}
        >
            {cells}
        </TableRow>
    )
})

type MarketDepthTotalRowProps = {
    securityKey: string
    side: DepthSide
}

const MarketDepthTotalRow = memo(function MarketDepthTotalRow({ securityKey, side }: MarketDepthTotalRowProps) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell

    return (
        <TableRow className={classes.tableRow}>
            <TableCell className={cx(classes.rowCell, sideClass)}>Total</TableCell>
            <TableCell colSpan={2} className={cx(classes.rowCell, sideClass)}>
                <DepthTotalValue securityKey={securityKey} side={side} />
            </TableCell>
        </TableRow>
    )
})

type MarketDepthSideProps = {
    securityKey: string
    side: DepthSide
    priceMultiplier: number
    bidAskCentered: boolean
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}

const MarketDepthSide = memo(function MarketDepthSide({
    securityKey,
    side,
    priceMultiplier,
    bidAskCentered,
    tradeable,
    onRowClick,
}: MarketDepthSideProps) {
    const { classes } = useStyles()
    const quoteCount = useOrderBookQuoteCount(securityKey, side)

    const headers = useMemo(() => {
        const label = side === 'bid' ? 'BIDS' : 'OFFERS'
        const headerCells = [
            <TableCell key="price" className={classes.headerCell}>
                <Typography variant="caption">{label}</Typography>
            </TableCell>,
            <TableCell key="orders" className={classes.headerCell}>
                <Typography variant="caption">ORDERS</Typography>
            </TableCell>,
            <TableCell key="qty" className={classes.headerCell}>
                <Typography variant="caption">QTY</Typography>
            </TableCell>,
        ]
        return side === 'bid' && bidAskCentered ? headerCells.reverse() : headerCells
    }, [side, bidAskCentered, classes.headerCell])

    const rows = quoteCount > 0
        ? Array.from({ length: quoteCount }, (_, index) => (
            <MarketDepthRowShell
                key={`${side}-${index}`}
                securityKey={securityKey}
                side={side}
                index={index}
                priceMultiplier={priceMultiplier}
                bidAskCentered={bidAskCentered}
                tradeable={tradeable}
                onRowClick={onRowClick}
            />
        ))
        : Array.from({ length: EMPTY_DEPTH_ROWS }, (_, index) => (
            <MarketDepthEmptyRow
                key={`empty-${side}-${index}`}
                side={side}
                bidAskCentered={bidAskCentered}
                tradeable={tradeable}
                onRowClick={onRowClick}
            />
        ))

    return (
        <Box width="50%">
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>{headers}</TableRow>
                </TableHead>
                <TableBody>
                    {rows}
                    <MarketDepthTotalRow securityKey={securityKey} side={side} />
                </TableBody>
            </Table>
        </Box>
    )
})

const MarketDepth: React.FC<MarketDepthProps> = ({ security, bidAskCentered = false, tradeable }) => {
    const dispatch = useDispatch()
    const securityKey = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const hasOrderBook = useHasOrderBook(securityKey)
    const priceMultiplier = security?.priceMultiplier ?? 100
    const isTradeable = tradeable ?? isTradeableSecurity(security)

    const onRowClick = useCallback((type: DepthSide, price: number) => {
        if (!isTradeable) return
        dispatch(setQuickOrderData({
            openDialog: true,
            mode: 'regular',
            type: 'trade',
            isSquareOffOrder: false,
            order: {
                transactionType: type === "bid" ? TransactionType.SELL : TransactionType.BUY,
                security: security,
                editOrder: undefined,
                brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                orderType: OrderType.NORMAL,
                product: Product.CNC,
                price: price,
                quantity: 1 * (security.lotSize ?? 1),
                maxQuantity: 0
            }
        }))
    }, [dispatch, security, isTradeable])

    if (!hasOrderBook) return null

    return (
        <Box display="flex" flexDirection="column" width="100%">
            <BuySellOrdersBar securityKey={securityKey} />
            <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                <MarketDepthSide
                    securityKey={securityKey}
                    side="bid"
                    priceMultiplier={priceMultiplier}
                    bidAskCentered={bidAskCentered}
                    tradeable={isTradeable}
                    onRowClick={onRowClick}
                />
                <MarketDepthSide
                    securityKey={securityKey}
                    side="ask"
                    priceMultiplier={priceMultiplier}
                    bidAskCentered={false}
                    tradeable={isTradeable}
                    onRowClick={onRowClick}
                />
            </Box>
        </Box>
    )
}

export default memo(MarketDepth)
